import { Close } from "@mui/icons-material";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import { formatDuration, intervalToDuration } from "date-fns";
import moment from "moment-timezone";
import React, { useState } from "react";
import { t } from "ttag";
import JsonTree from "../../components/JsonTree/JsonTree";
import { IQueueFlowNode, IQueueJobDetails } from "../../models/Queues.types";
import { describeRetention, hasRetention } from "./queueRetention";
import useStyles from "./QueueJobPopup.style";

type JobTab = "data" | "progress" | "options" | "logs" | "error" | "flow";

interface IQueueJobPopupProps {
  queueId: string;
  queueName: string;
  queuePrefix?: string;
  jobId: string;
  /** null while the job is loading */
  job: IQueueJobDetails | null;
  onClose: () => void;
}

const formatTimestamp = (timestamp?: number) => (timestamp ? moment(timestamp).format("llll") : "-");

// The flow tab only exists for jobs that are part of a parent/child flow.
const getTabs = (hasFlow: boolean): Array<{ tab: JobTab; label: string }> => [
  { tab: "data", label: t`Data` },
  { tab: "progress", label: t`Progress` },
  { tab: "options", label: t`Options` },
  { tab: "logs", label: t`Logs` },
  { tab: "error", label: t`Error` },
  ...(hasFlow ? [{ tab: "flow" as JobTab, label: t`Flow` }] : [])
];

const formatMs = (ms: number) =>
  ms < 1000 ? `${ms} ms` : formatDuration(intervalToDuration({ start: 0, end: ms })) || `${ms} ms`;

/** How long a job waited before a worker took it, and how long the worker ran it. */
const getTimings = (job: IQueueJobDetails) => ({
  waited: job.timestamp && job.processedOn ? Math.max(0, job.processedOn - job.timestamp) : undefined,
  ran: job.processedOn && job.finishedOn ? Math.max(0, job.finishedOn - job.processedOn) : undefined
});

interface IFlowTreeProps {
  node: IQueueFlowNode;
  currentJobId: string;
  currentQueueId: string;
  classes: any;
}

const FlowTree: React.FC<IFlowTreeProps> = ({ node, currentJobId, currentQueueId, classes }) => (
  <div className={classes.flowNode}>
    <div className={node.id === currentJobId && node.queueId === currentQueueId ? classes.flowCurrent : classes.flowRow}>
      <span className={classes.flowName}>{node.name}</span>
      <span className={classes.flowMeta}>
        {node.queueName} #{node.id}
      </span>
      <span className={classes.flowState}>{node.state}</span>
      {node.truncated && <span className={classes.flowMeta}>{t`more not shown`}</span>}
    </div>
    {node.children.map((child) => (
      <FlowTree
        key={`${child.queueId}-${child.id}`}
        node={child}
        currentJobId={currentJobId}
        currentQueueId={currentQueueId}
        classes={classes}
      />
    ))}
  </div>
);

const QueueJobPopup: React.FC<IQueueJobPopupProps> = ({ queueId, queueName, queuePrefix, jobId, job, onClose }) => {
  const classes: any = useStyles();
  const [tab, setTab] = useState<JobTab>("data");

  const renderOverviewField = (label: string, value: string) => (
    <div className={classes.overviewField} key={label}>
      <Typography className={classes.fieldLabel}>{label}</Typography>
      <Typography className={classes.fieldValue}>{value}</Typography>
    </div>
  );

  const renderEmpty = (text: string) => <Typography className={classes.emptyText}>{text}</Typography>;

  const renderTab = (details: IQueueJobDetails) => {
    switch (tab) {
      case "data":
        return <JsonTree value={{ jobData: details.data, returnValue: details.returnValue }} />;
      case "progress":
        return <JsonTree value={details.progress ?? 0} />;
      case "options":
        return <JsonTree value={details.opts || {}} />;
      case "logs":
        return details.logs?.length ? (
          <pre className={classes.codeBlock}>{details.logs.join("\n")}</pre>
        ) : (
          renderEmpty(t`No logs.`)
        );
      case "flow":
        if (details.flowError) {
          return renderEmpty(t`The flow of this job could not be read right now.`);
        }

        return details.flow ? (
          <FlowTree node={details.flow} currentJobId={jobId} currentQueueId={queueId} classes={classes} />
        ) : (
          renderEmpty(t`This job is not part of a flow.`)
        );
      default:
        return details.failedReason || details.stacktrace?.length ? (
          <>
            {!!details.failedReason && (
              <Typography className={classes.failedReason}>{details.failedReason}</Typography>
            )}
            {!!details.stacktrace?.length && <pre className={classes.codeBlock}>{details.stacktrace.join("\n\n")}</pre>}
          </>
        ) : (
          renderEmpty(t`This job has no error.`)
        );
    }
  };

  const renderContent = () => {
    if (!job) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    const attempts = job.maxAttempts ? `${job.attemptsMade || 0} / ${job.maxAttempts}` : `${job.attemptsMade || 0}`;
    const { waited, ran } = getTimings(job);

    return (
      <>
        <div className={classes.overviewGrid}>
          {renderOverviewField(t`Queue`, queuePrefix ? `${queueName} (${queuePrefix})` : queueName)}
          {renderOverviewField(t`Name`, job.name)}
          {renderOverviewField(t`Added`, formatTimestamp(job.timestamp))}
          {renderOverviewField(t`Started`, formatTimestamp(job.processedOn))}
          {renderOverviewField(t`Finished`, formatTimestamp(job.finishedOn))}
          {hasRetention(job.retention) &&
            renderOverviewField(
              t`Expires`,
              job.expiresAt ? formatTimestamp(job.expiresAt) : describeRetention(job.retention)
            )}
          {hasRetention(job.retention) && !!job.expiresAt && renderOverviewField(t`Retention`, describeRetention(job.retention))}
          {renderOverviewField(t`Attempts`, attempts)}
          {waited !== undefined && renderOverviewField(t`Waited`, formatMs(waited))}
          {ran !== undefined && renderOverviewField(t`Ran for`, formatMs(ran))}
          {!!job.delay && renderOverviewField(t`Delay`, formatMs(job.delay))}
          {!!job.processedBy && renderOverviewField(t`Processed by`, job.processedBy)}
          {job.attemptsStarted !== undefined &&
            job.attemptsStarted !== job.attemptsMade &&
            renderOverviewField(t`Attempts started`, String(job.attemptsStarted))}
        </div>

        <Divider className={classes.divider} />

        <Tabs value={tab} onChange={(_event, nextTab: JobTab) => setTab(nextTab)} className={classes.tabs}>
          {getTabs(!!job.flow || !!job.flowError).map(({ tab: value, label }) => (
            <Tab key={value} value={value} label={label} className={classes.tab} />
          ))}
        </Tabs>
        <div className={classes.tabContent}>{renderTab(job)}</div>
      </>
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.titleLeft}>
          <Typography className={classes.titleText}>{t`Job Details`}</Typography>
          <Typography className={classes.titleJobId}>{jobId}</Typography>
        </div>
        <IconButton size="small" aria-label={t`Close`} onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>{renderContent()}</DialogContent>
    </Dialog>
  );
};

export default QueueJobPopup;
